import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { apiInstance } from '@/utils';
import type { AccountPriority } from '@/types/prospects';

export type F5BotAlert = {
  id: number;
  vendorid: number;
  keyword: string;
  platform: string | null;
  subreddits: string[];
  enabled: boolean;
  created_at: string;
};

export type F5BotConfig = {
  alerts: F5BotAlert[];
  enabled: boolean;
  subreddits: string[];
};

export type F5BotConfigPayload = {
  keywords: string[];
  enabled: boolean;
  subreddits: string[];
};

type F5BotConfigResponse = {
  config: F5BotConfig | null;
  has_config: boolean;
};

export const getF5BotConfig = async (): Promise<F5BotConfigResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.f5botConfigs,
  });

  return result.data;
};

export const saveF5BotConfig = async (
  data: F5BotConfigPayload,
): Promise<F5BotConfigResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.f5botConfigs,
    data,
  });

  return result.data;
};

// LinkedIn mention config types and API functions

export type LinkedinMentionKeyword = {
  id: number;
  keyword: string;
  created_at: string;
};

export type LinkedinMentionConfig = {
  keywords: LinkedinMentionKeyword[];
  enabled: boolean;
};

export type LinkedinMentionConfigPayload = {
  keywords: string[];
  enabled: boolean;
};

type LinkedinMentionConfigResponse = {
  config: LinkedinMentionConfig | null;
  has_config: boolean;
};

export const getLinkedinMentionConfig = async (): Promise<LinkedinMentionConfigResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.linkedinMentionConfigs,
  });
  return result.data;
};

export const saveLinkedinMentionConfig = async (
  data: LinkedinMentionConfigPayload,
): Promise<LinkedinMentionConfigResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.linkedinMentionConfigs,
    data,
  });
  return result.data;
};

export type CommunityMention = {
  id: string;
  signal_source: string;
  ai_fit_label: string | null;
  ai_fit_score: number | null;
  account_priority?: AccountPriority | null;
  account_priority_enabled?: boolean;
  account_priority_available?: boolean;
  outreach_eligibility?: NonNullable<AccountPriority['routing']>['outreachEligibility'];
  ai_fit_reasoning: string | string[] | null;
  post_url: string | null;
  post_title: string | null;
  keyword: string | null;
  keywords: string[] | null;
  platform: string | null;
  author: string | null;
  date_published: string | null;
  instruction: string | null;
  author_headline: string | null;
  author_linkedin_url: string | null;
  author_profile_pic: string | null;
  person_linkedin_url: string | null;
  person_title: string | null;
  person_email: string | null;
  enriched_company_name: string | null;
  enriched_company_domain: string | null;
  company_domain: string | null;
  company_name: string | null;
  created_at: string;
};

export type CommunityMentionsResponse = {
  mentions: CommunityMention[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  features?: {
    accountPriorityEnabled: boolean;
  };
};

export type CommunityMentionsParams = {
  page?: number;
  pageSize?: number;
  timeFilter?: string;
  search?: string;
  signalSource?: string;
  sortBy?: 'created_at' | 'last_activity' | 'account_priority';
  sortDirection?: 'asc' | 'desc';
  /** SDR scope: 'me' | 'all' | 'unassigned' | workspace user id (mb-frontend#4465). */
  assignee?: string;
};

export const getCommunityMentions = async (
  params: CommunityMentionsParams = {},
): Promise<CommunityMentionsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.communityMentions,
    params: {
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      timeFilter: params.timeFilter || '30d',
      ...(params.search ? { search: params.search } : {}),
      ...(params.signalSource ? { signalSource: params.signalSource } : {}),
      ...(params.assignee && params.assignee !== 'all' ? { assignee: params.assignee } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortDirection ? { sortDirection: params.sortDirection } : {}),
    },
  });

  return result.data;
};
